import React, { useEffect, useState } from "react";
import { getOffersFromLogbuy } from "../../utils/api";

interface Offer {
  imageUrl: string;
  name: string;
  teaser: string;
  description: string;
  discountText: string;
  externalUrl: string;
  featured: boolean;
  supplierId: number;
}

export default function VismaLogbuyOffers() {

  const featuredOfferIds = [19457, 19088, 1985, 440, 18920, 19389, 20885, 2940, 19335, 19281, 19504, 19068];


  const [offers, setOffers] = useState<Offer[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOffer, setSelectedOffer] = useState<Offer | null>(null);
  const loadingMessage = "henter tilbud...";
  const loadingAnimationStepMs = 90;
  const minLoadingDurationMs = loadingMessage.length * loadingAnimationStepMs;
  const [animatedLoadingText, setAnimatedLoadingText] = useState("");

  // State to manage displayed data and load more button
  const [displayedData, setDisplayedData] = useState<Offer[]>([]);
  const [visibleItemCount, setVisibleItemCount] = useState(9); // Change this as needed

  const [ctaUrl, setCtaUrl] = useState<string>("");

  const form = document.querySelector(`[data-component='logbuy-offers']`) as HTMLElement;

  useEffect(() => {
    let isMounted = true;

    const fetchOffers = async () => {
      const loadingStart = Date.now();

      try {
        const response = await getOffersFromLogbuy(2000);

        if (response && response.data.offers && response.data.offers.length > 0) {
          const featuredOrderMap = new Map(featuredOfferIds.map((supplierId, index) => [supplierId, index]));
          const sortedOffers = [...response.data.offers].sort((a, b) => {
            const aOrder = featuredOrderMap.get(a.supplierId);
            const bOrder = featuredOrderMap.get(b.supplierId);

            if (aOrder !== undefined && bOrder !== undefined) {
              return aOrder - bOrder;
            }

            if (aOrder !== undefined) {
              return -1;
            }

            if (bOrder !== undefined) {
              return 1;
            }

            return 0;
          });

          setOffers(sortedOffers);
          setDisplayedData(sortedOffers.slice(0, visibleItemCount));

          // Remove the hidden class from the section
          const section = document.querySelector('[data-component="logbuy-offers"]');
          if (section) {
            section.classList.remove("hidden");
          }
        }
      } catch (error) {
        console.error("Error fetching logbuy offers:", error);
      } finally {
        const elapsed = Date.now() - loadingStart;
        const remaining = Math.max(0, minLoadingDurationMs - elapsed);

        if (remaining > 0) {
          await new Promise<void>((resolve) => {
            window.setTimeout(resolve, remaining);
          });
        }

        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchOffers();

    return () => {
      isMounted = false;
    };
  }, [minLoadingDurationMs]);

  useEffect(() => {
    if (!loading) {
      setAnimatedLoadingText("");
      return;
    }

    let charIndex = 0;
    setAnimatedLoadingText("");

    const intervalId = window.setInterval(() => {
      charIndex += 1;
      setAnimatedLoadingText(loadingMessage.slice(0, charIndex));

      if (charIndex >= loadingMessage.length) {
        window.clearInterval(intervalId);
      }
    }, loadingAnimationStepMs);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [loading, loadingMessage, loadingAnimationStepMs]);

  useEffect(() => {
    if (!selectedOffer) {
      return;
    }

    if (form?.dataset.resultUrl && form.dataset.resultUrl !== "") {
      const ctaUrl = form.dataset.resultUrl;
      setCtaUrl(ctaUrl);
    }

    const handleEscClose = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setSelectedOffer(null);
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleEscClose);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleEscClose);
    };
  }, [selectedOffer]);

  // Handler for the "Load More" button
  const handleLoadMore = () => {
    // Increase the visible item count (you can adjust this logic based on your requirements)
    setVisibleItemCount((prevCount) => prevCount + 9);

    // Update displayedData with additional items
    setDisplayedData(offers.slice(0, visibleItemCount + 9));
  };

  const handleOfferClick = (event: React.MouseEvent<HTMLAnchorElement>, offer: Offer) => {
    event.preventDefault();
    setSelectedOffer(offer);
  };

  const closeOfferOverlay = () => {
    setSelectedOffer(null);
  };

  const hasMoreOffers = displayedData.length < offers.length;

  if (loading || offers.length === 0) {
    if (loading) {
      return <div className="logbuy-loading-text">{animatedLoadingText}</div>;
    }
    return null;
  }

  return (
    <>
      <div className="logbuy-offers-container">
        <div className="row">
          {displayedData.map((offer, index) => (
            <div key={index} className="col-xs-12 col-md-4 mb-4">
              <a className="logbuy-offer-link" href="#" rel="noopener noreferrer" onClick={(event) => handleOfferClick(event, offer)}>
                <img src={offer.imageUrl} alt={offer.name} className="subnavigation-image logbuy-offer-image" />
                <div className="logbuy-offer-discount">{offer.discountText !== "Tilbud" ? `Spar ${offer.discountText}` : offer.discountText}</div>
                <div className="linklist-item logbuy-offer-content">
                  <span className="logbuy-offer-title-row">
                    <h2 className="logbuy-offer-title">{offer.name}</h2>
                  </span>
                  <p className="logbuy-offer-teaser">{offer.teaser}</p>
                </div>
              </a>
            </div>
          ))}
        </div>
        {hasMoreOffers && (
          <div className="col-12 logbuy-load-more-container">
            <button className="logbuy-load-more-button" onClick={handleLoadMore}>
              Indlæs flere
            </button>
          </div>
        )}

        {selectedOffer && (
          <div className="logbuy-offer-overlay" onClick={closeOfferOverlay} role="presentation">
            <div className="logbuy-offer-modal" role="dialog" aria-modal="true" aria-labelledby="logbuy-offer-title" onClick={(event) => event.stopPropagation()}>
              <button className="logbuy-offer-modal-close" onClick={closeOfferOverlay} aria-label="Luk detaljer">
                Luk
              </button>
              <img src={selectedOffer.imageUrl} alt={selectedOffer.name} className="logbuy-offer-modal-image" />
              <div className="logbuy-offer-discount">{selectedOffer.discountText !== "Tilbud" ? `Spar ${selectedOffer.discountText}` : selectedOffer.discountText}</div>
              <h2 id="logbuy-offer-title" className="logbuy-offer-modal-title">
                {selectedOffer.name}
              </h2>
              <p className="logbuy-offer-modal-description">{selectedOffer.description}</p>
              <div className="logbuy-offer-modal-footer">
                <a href={ctaUrl} rel="noopener noreferrer" className="logbuy-offer-modal-signup-button">
                  Ja tak til medlemsskab
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
